'use client'
import { LoadingScreen } from '@/components'
import { useLoginGoogle } from '@/features/auth'
import { useFacebookCookieTracker } from '@/features/save-log'
import { useCookieHttpOnly } from '@/hooks'
import { useAccountStore } from '@/store'
import { GoogleAnalytics, GoogleTagManager } from '@next/third-parties/google'
import Script from 'next/script'
import { useEffect, useState } from 'react'
import { FacebookMessengerButton } from './facebook-messenger-button'
import { FirstUpdateProfile } from './first-update-profile'
import { FloatingButtonsContainer } from './floating-buttons-container'
import { UpdateAffiliateClick } from './update-affiliate-click'
import { ReferralSourceLogin } from './referral-source-login'
import { SendFacebookPageView } from './send-facebook-page-view'
import { ZaloButton } from './zalo-button'

type Props = {}

// Kiểm tra môi trường production
const isProduction = process.env.NEXT_PUBLIC_MODE === 'production'

// nơi để nhúng script GA, FB pixel,Zalo, chatbot, ...
export const ScriptClient = (props: Props) => {
  const { cookie: token, refetchCookie } = useCookieHttpOnly({
    cookieName: 'token',
  })
  const { clearUser, updateUser } = useAccountStore()

  // Timestamp dùng để cache-bust widget.js của Hydra.
  // Sinh sau khi mount để tránh hydration mismatch giữa server và client.
  const [hydraWidgetVersion, setHydraWidgetVersion] = useState('')
  useEffect(() => {
    setHydraWidgetVersion(Date.now().toString())
  }, [])

  useEffect(() => {
    // if (!token) return clearUser()
    if (!token) return
    try {
      updateUser()
    } finally {
    }
  }, [token, clearUser, updateUser])

  // Track và update Facebook cookie khi có thay đổi
  // Chỉ chạy khi user đã đăng nhập (có token)
  useFacebookCookieTracker({
    debounceTime: 2000, // Debounce 2 giây
    enabled: !!token, // Chỉ enable khi có token
  })

  // xử lý đăng nhập google
  const { isLoading } = useLoginGoogle()
  if (isLoading) {
    return (
      <div className="fixed left-0 top-0 z-50 flex h-full w-screen items-center justify-center bg-white">
        <LoadingScreen />
      </div>
    )
  }
  return (
    <>
      {/* Popup cập nhật mật khẩu lần đầu cho user đăng nhập bằng OTP */}
      {/* <FirstUpdateProfile /> */}

      {/* Track affiliate click từ URL ref parameter */}
      <UpdateAffiliateClick />

      {/* Track referral source login */}
      <ReferralSourceLogin />

      {/* Send Facebook PageView tracking khi load/refresh trang */}
      <SendFacebookPageView />

      {/* Các nút chat cố định - tự sắp xếp từ dưới lên khi thêm item mới */}
      <FloatingButtonsContainer>
        <ZaloButton />
        <FacebookMessengerButton />
      </FloatingButtonsContainer>

      {/* Google Tag Manager (GTM-WBPTCGZ6) - Inject to HEAD - chỉ chạy ở production */}
      {isProduction && (
        <Script
          id="gtm-script"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-WBPTCGZ6');
            `,
          }}
        />
      )}

      {/* Microsoft Clarity - Inject to HEAD - chỉ chạy ở production */}
      {isProduction && (
        <Script
          id="clarity-script"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "tynjx8mtuo");
            `,
          }}
        />
      )}

      {/* Facebook Pixel - Inject to HEAD - chỉ chạy ở production */}
      {isProduction && (
        <>
          <Script
            id="facebook-pixel"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '1394962155605018');
                fbq('track', 'PageView');
              `,
            }}
          />
          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              src="https://www.facebook.com/tr?id=1394962155605018&ev=PageView&noscript=1"
              alt=""
            />
          </noscript>
        </>
      )}

      {/* TikTok Pixel - Inject to HEAD - chỉ chạy ở production */}
      {isProduction && (
        <Script
          id="tiktok-pixel"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              !function (w, d, t) {
                w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t][],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
                ttq.load('D442VSRC77U3U3UTUJOG');
                ttq.page();
              }(window, document, 'ttq');
            `,
          }}
        />
      )}

      {/* Google Analytics - Inject to HEAD - chỉ chạy ở production */}
      {isProduction && (
        <>
          <Script
            src="https://www.googletagmanager.com/gtag/js?id=G-J73TV0ML8W"
            strategy="afterInteractive"
          />
          <Script
            id="google-analytics"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-J73TV0ML8W');
              `,
            }}
          />
        </>
      )}

      {/* Google Tag Manager noscript - Inject to BODY start */}
      {isProduction && (
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-WBPTCGZ6"
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
      )}

      {hydraWidgetVersion && (
        <Script
          id="hydra-chat-widget"
          src={`https://cc.hydrashop.vn/widget.js?v=4&version=${hydraWidgetVersion}`}
          strategy="afterInteractive"
          data-api="https://cc.hydrashop.vn"
          data-brand="hydra"
          data-bot-name="Dê cười"
          data-bot-subtitle="Tư vấn viên Hydra — đang online"
          data-avatar="https://cc.hydrashop.vn/assets/de-cuoi.png?v=2"
          data-accent="#0a0a0a"
          data-accent-secondary="#454545"
          data-privacy-url="https://hydrashop.vn"
          data-footer=""
          data-greet="Dê cười bên Hydra đây 👋 Anh/chị mặc cả ngày — em chat cả ngày!"
          data-greet-follow="Anh/chị tìm áo hay quần để em tư vấn cho nha 🔥"
        />
      )}

      {process.env.NEXT_PUBLIC_GOOGLE_TAG_ANALYTICS_ID && (
        <GoogleAnalytics gaId={process.env.NEXT_PUBLIC_GOOGLE_TAG_ANALYTICS_ID} />
      )}
      {process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID && (
        <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID} />
      )}
    </>
  )
}
